'use client';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import StoreIcon from '../components/StoreIcon';
import AssetPreview, { type AssetPreviewMode } from '../components/AssetPreview';
import { formatMinorMoney } from '../packages/commerce-offer/index';
import { CATALOG_PAGE_SIZE, filterAndSortCatalog, paginateCatalog, type CatalogSort, type CatalogPrice } from './catalog-model';
import { catalogCopy } from './catalog-copy';
import { useLocale } from '../lib/i18n';
import { getStoredSession, onAuthStateChange, type AuthSession } from '../lib/supabase/client';
import { loadGuestWishlist, mergeGuestWishlistIntoAccount, saveAccountWishlist, saveGuestWishlist } from '../lib/supabase/wishlist';
import { GhibliHeroVfx } from '../components/GhibliHeroVfx';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';

export interface ManifestItem {
  id: string; name: string; category: string; tags?: string[]; description: string; slug?: string; family?: string;
  is_character?: boolean; folder: string; default_animation: string; license?: string;
  animations: Record<string, { name: string; png: string; json: string; frames: number; frame_size: { w: number; h: number } }>;
  source?: 'static' | 'published'; listing_id?: string; asset_version?: string; preview_url?: string;
  preview_mode?: AssetPreviewMode; detail_href?: string; distribution_mode?: 'free' | 'paid'; currency?: string;
  price_minor?: number; published_at?: string; adapter_ids?: string[]; capabilities?: string[];
}

type PublishedCatalogItem = {
  listing_id: string; title: string; summary: string; category: string; asset_id: string;
  asset_version: string; slug: string; license_id: string | null; license_version: string | null;
  distribution_mode: 'free' | 'paid'; currency: string; price_minor: number; published_at: string; capabilities: string[];
  adapter_ids: string[]; preview_url: string; preview_mode: AssetPreviewMode;
};

function toManifestItem(item: PublishedCatalogItem): ManifestItem {
  if (!item.listing_id || !item.asset_id || !item.asset_version || !item.slug) throw new Error('Published catalog item lineage is incomplete');
  if (item.preview_mode !== 'static' && item.preview_mode !== 'animated') throw new Error('Published catalog preview mode is missing or invalid');
  if (!item.preview_url) throw new Error('Published catalog preview is missing');
  if (!Number.isSafeInteger(item.price_minor) || item.price_minor < 0) throw new Error('Published catalog price_minor is invalid');
  if (!item.published_at || !Number.isFinite(Date.parse(item.published_at))) throw new Error('Published catalog published_at is invalid');
  return {
    id: item.listing_id, name: item.title, category: item.category || 'Asset',
    tags: [...(item.capabilities || []), ...(item.adapter_ids || [])], description: item.summary,
    folder: '', default_animation: '', animations: {},
    license: item.license_id && item.license_version ? `${item.license_id}@${item.license_version}` : item.license_id || undefined,
    slug: item.slug, source: 'published', listing_id: item.listing_id, asset_version: item.asset_version,
    preview_url: item.preview_url, preview_mode: item.preview_mode, detail_href: `/assets/${item.slug}`,
    distribution_mode: item.distribution_mode, currency: item.currency, price_minor: item.price_minor, published_at: item.published_at,
    adapter_ids: item.adapter_ids, capabilities: item.capabilities,
  };
}

export default function CatalogView({ items }: { items: ManifestItem[] }) {
  const { t, locale } = useLocale();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [sort, setSort] = useState<CatalogSort>('featured');
  const [price, setPrice] = useState<CatalogPrice>('all');
  const [adapter, setAdapter] = useState('all');
  const [catalogError, setCatalogError] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogAttempt, setCatalogAttempt] = useState(0);
  const [list, setList] = useState(false);
  const [saved, setSaved] = useState<string[]>([]);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [wishlistError, setWishlistError] = useState('');
  const [onlySaved, setOnlySaved] = useState(false);
  const [page, setPage] = useState(1);
  const [publishedItems, setPublishedItems] = useState<ManifestItem[]>([]);
  const searchRef = useRef<HTMLInputElement>(null);
  const catalogRef = useRef<HTMLElement>(null);

  useEffect(() => {
    let cancelled = false;
    const applySession = async (next: AuthSession | null) => {
      setSession(next);
      setWishlistError('');
      if (!next) {
        setSaved(loadGuestWishlist());
        return;
      }
      try {
        const merged = await mergeGuestWishlistIntoAccount(next);
        if (!cancelled) setSaved(merged);
      } catch (reason: any) {
        if (!cancelled) {
          setSaved(loadGuestWishlist());
          if (reason?.message !== 'session_expired' && reason?.message !== 'account_profile_unavailable') {
            setWishlistError(reason instanceof Error ? reason.message : 'Wishlist unavailable');
          }
        }
      }
    };
    void applySession(getStoredSession());
    const unsubscribe = onAuthStateChange(next => { void applySession(next); });
    return () => { cancelled = true; unsubscribe(); };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError(false);
    const controller = new AbortController();
    fetch('/api/asset-catalog', { signal: controller.signal, cache: 'no-store' })
      .then(async response => {
        if (!response.ok) {
          const errText = await response.text().catch(() => '');
          console.warn('[CatalogView] Published catalog unavailable:', response.status, errText);
          throw new Error(`Published catalog unavailable (${response.status})`);
        }
        const payload = await response.json();
        if (!Array.isArray(payload.items)) throw new Error('Published catalog response is invalid');
        const mapped = payload.items.map(toManifestItem);
        if (!cancelled) setPublishedItems(mapped);
      })
      .catch(reason => {
        if (!cancelled && reason?.name !== 'AbortError') setCatalogError(true);
      })
      .finally(() => { if (!cancelled) setCatalogLoading(false); });
    return () => { cancelled = true; controller.abort(); };
  }, [catalogAttempt]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault(); searchRef.current?.focus(); searchRef.current?.scrollIntoView({ block: 'center' });
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const staticIds = new Set(items.map(item => item.id));
  const seenPublishedKeys = new Set<string>();
  const uniquePublished: ManifestItem[] = [];
  for (const p of publishedItems) {
    const dedupeKey = p.slug || p.listing_id || p.id;
    if (staticIds.has(p.id) || seenPublishedKeys.has(dedupeKey)) continue;
    seenPublishedKeys.add(dedupeKey);
    uniquePublished.push(p);
  }
  const catalogItems: ManifestItem[] = [
    ...items.map(item => ({ ...item, source: item.source ?? 'static' as const })),
    ...uniquePublished,
  ];
  const categories = ['All', ...new Set(catalogItems.flatMap(item => [item.category, ...(item.tags ?? [])]))];
  const adapters = [...new Set(catalogItems.flatMap(item => item.adapter_ids ?? []))].sort();
  const filtered = filterAndSortCatalog(catalogItems, { query: search, category, price, adapter, onlySaved, savedIds: saved, sort });
  const pagination = paginateCatalog(filtered, page, CATALOG_PAGE_SIZE);

  const goToPage = (nextPage: number) => {
    setPage(nextPage);
    catalogRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const toggleSaved = async (item: ManifestItem) => {
    const previous = saved;
    const isAdding = !previous.includes(item.id);
    const next = isAdding ? [...previous, item.id] : previous.filter(id => id !== item.id);
    setSaved(next);
    setWishlistError('');
    if (onlySaved) setPage(1);

    if (isAdding) {
      toast.success(locale === 'vi' ? `Đã thêm "${item.name}" vào yêu thích` : `Added "${item.name}" to favorites`);
    } else {
      toast.info(locale === 'vi' ? `Đã xóa "${item.name}" khỏi yêu thích` : `Removed "${item.name}" from favorites`);
    }

    try {
      if (session) await saveAccountWishlist(session, next);
      else saveGuestWishlist(next);
    } catch (reason) {
      setSaved(previous);
      const msg = reason instanceof Error ? reason.message : 'Wishlist update failed';
      setWishlistError(msg);
      toast.error(msg);
    }
  };

  const resetFilters = () => { setSearch(''); setCategory('All'); setPrice('all'); setAdapter('all'); setOnlySaved(false); setPage(1); };

  return <>
    <section
      className="min-h-[420px] md:min-h-[460px] grid place-items-center relative overflow-hidden bg-cover bg-center border-b border-border/60"
      style={{ backgroundImage: "linear-gradient(90deg,#00101a12,#00101a38 40%,#00101a15),url('/storefront/moonlit-lake.webp')" }}
      aria-labelledby="hero-title"
    >
      <GhibliHeroVfx imageSrc="/storefront/moonlit-lake.webp" />
      <div className="relative z-10 max-w-4xl w-full px-4 md:px-6 py-10 md:py-12 text-center">
        <span className="text-primary text-xs tracking-widest font-bold block mb-3 drop-shadow">
          ENGINE-READY 2D ASSETS
        </span>
        <h1 id="hero-title" className="text-white font-serif font-bold text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-tight drop-shadow-md">
          Game-Ready Pixel Art & <em className="text-amber-300 not-italic">Production Packs</em>
        </h1>
        <p className="text-slate-100 max-w-2xl mx-auto my-4 text-sm md:text-base leading-relaxed drop-shadow">
          Characters, monsters, environments and complete animation suites engineered for production.<br className="hidden sm:inline"/>Transparent PNG sheets, normal maps and JSON atlases, ready for your game engine.
        </p>
        <ul className="flex flex-wrap items-center justify-center gap-4 sm:gap-7 text-xs sm:text-sm text-slate-200 mb-6">
          <li className="flex items-center gap-2 font-medium"><StoreIcon name="sparkle"/>Commercial License Included</li>
          <li className="flex items-center gap-2 font-medium"><StoreIcon name="heart"/>Engine-Ready (Godot, Unity, Phaser)</li>
          <li className="flex items-center gap-2 font-medium"><StoreIcon name="download"/>{catalogItems.length} Production Assets</li>
        </ul>
      <div className="catalog-search flex items-center gap-3 max-w-2xl mx-auto mt-6 px-4 py-1.5 border border-border/70 rounded-md bg-card/80 backdrop-blur shadow-md focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20">
        <StoreIcon name="search" />
        <Input
          id="sprite-search"
          ref={searchRef}
          value={search}
          onInput={event => { setSearch(event.currentTarget.value); setPage(1); }}
          placeholder={`Search ${catalogItems.length} assets and packs… character, monster, tileset`}
          aria-label="Search production assets"
          aria-controls="catalog-results"
          type="search"
          className="border-0 bg-transparent h-9 px-0 text-sm focus-visible:ring-0 focus-visible:outline-none"
        />
        {search ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-muted-foreground hover:text-foreground rounded"
            onClick={() => { setSearch(''); setPage(1); }}
            aria-label="Clear search"
          >
            <StoreIcon name="close" />
          </Button>
        ) : (
          <kbd className="hidden sm:inline-block whitespace-nowrap border border-border rounded px-2 py-0.5 text-xs text-muted-foreground">Ctrl K</kbd>
        )}
      </div>
    </div></section>
    <section ref={catalogRef} className="border-b border-border" id="catalog" aria-label="Pixel art production asset catalog">
      <div className="flex flex-col md:flex-row w-full min-h-[600px] items-stretch">
        <aside className="w-full md:w-[260px] md:min-w-[260px] shrink-0 border-b md:border-b-0 md:border-r border-border bg-[#0c1117] p-5 flex flex-col gap-3" aria-label="Filter by tags and categories">
          <div className="flex items-center justify-between pb-2.5 border-b border-border">
            <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <StoreIcon name="filter"/> Tags & Categories
            </span>
            {category !== 'All' && (
              <Button
                type="button"
                variant="link"
                size="sm"
                className="h-auto p-0 text-xs text-primary"
                onClick={() => { setCategory('All'); setPage(1); }}
                aria-label="Reset category filter"
              >
                Reset
              </Button>
            )}
          </div>
          <div className="flex flex-col gap-3.5 my-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground block">
                {catalogCopy.price}
              </label>
              <Select value={price} onValueChange={(val) => { setPrice(val as CatalogPrice); setPage(1); }}>
                <SelectTrigger className="w-full h-8 bg-card border-border text-xs rounded-md">
                  <SelectValue placeholder={catalogCopy.price} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('catalog.filter_all') === 'All' ? catalogCopy.allPrices : 'Mọi mức giá'}</SelectItem>
                  <SelectItem value="free">{t('catalog.filter_free')}</SelectItem>
                  <SelectItem value="paid">{t('catalog.filter_paid')}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-muted-foreground block">
                {catalogCopy.compatibility}
              </label>
              <Select value={adapter} onValueChange={(val) => { setAdapter(val); setPage(1); }}>
                <SelectTrigger className="w-full h-8 bg-card border-border text-xs rounded-md">
                  <SelectValue placeholder={catalogCopy.compatibility} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('catalog.engine_all')}</SelectItem>
                  {adapters.map(id => (
                    <SelectItem key={id} value={id}>
                      {catalogCopy.adapterNames[id] ?? id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {(price !== 'all' || adapter !== 'all' || onlySaved || search) && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs text-primary h-auto py-1 px-2 self-start rounded hover:underline"
                onClick={resetFilters}
              >
                {catalogCopy.reset}
              </Button>
            )}
          </div>
          <div className="flex md:flex-col gap-1 overflow-x-auto md:overflow-y-auto md:max-h-[calc(100vh-120px)] md:sticky md:top-5 pr-0.5 scrollbar-thin" aria-label="Filter by category">
            {categories.map(cat => {
              const count = cat === 'All' ? catalogItems.length : catalogItems.filter(it => it.category === cat || it.tags?.includes(cat)).length;
              return (
                <button
                  key={cat}
                  type="button"
                  className={cn(
                    'flex items-center justify-between gap-2.5 w-full rounded-md px-3 py-2 text-xs text-left transition-colors shrink-0 md:shrink border border-transparent',
                    category === cat
                      ? 'bg-primary/10 border-primary/40 text-primary font-bold'
                      : 'text-muted-foreground hover:border-border hover:bg-[#141c25] hover:text-white'
                  )}
                  aria-pressed={category === cat}
                  onClick={() => { setCategory(cat); setPage(1); }}
                >
                  <span className="truncate flex-1 capitalize">{cat}</span>
                  <span className={cn(
                    'text-[11px] px-1.5 py-0.5 rounded font-medium',
                    category === cat ? 'bg-primary text-primary-foreground font-bold' : 'bg-secondary text-muted-foreground'
                  )}>{count}</span>
                </button>
              );
            })}
          </div>
        </aside>
        <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 flex flex-col" aria-busy={catalogLoading}>
          {catalogError && (
            <div className="flex items-center justify-between gap-4 flex-wrap p-4 mb-4 border border-destructive/50 bg-destructive/10 rounded-md text-destructive" role="alert">
              <p>{catalogCopy.unavailable}</p>
              <Button type="button" variant="secondary" size="sm" onClick={() => setCatalogAttempt(current => current + 1)} className="rounded-md">
                {catalogCopy.retry}
              </Button>
            </div>
          )}
          {wishlistError && (
            <div className="p-4 mb-4 border border-destructive/50 bg-destructive/10 rounded-md text-destructive" role="alert">
              <p>Wishlist: {wishlistError}</p>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 mb-4 flex-wrap text-sm text-muted-foreground">
            <p id="catalog-result-count" role="status" aria-live="polite" className="text-sm text-muted-foreground">
              <strong className="text-foreground font-semibold">{filtered.length}</strong> {filtered.length === 1 ? 'asset' : 'assets'}
              <span className="hidden sm:inline"> · Ready for your next adventure</span>
              {catalogLoading && <span className="skeleton-shimmer inline-block w-12 h-3 rounded ml-2 align-middle" aria-label="Loading" />}
            </p>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant={onlySaved ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setOnlySaved(current => !current); setPage(1); }}
                aria-label={`Show favorites (${saved.length})`}
                aria-pressed={onlySaved}
                className={cn(
                  'h-9 border-border rounded-md transition-colors shrink-0',
                  saved.length > 0 ? 'px-3 gap-1.5' : 'w-9 px-0 justify-center',
                  onlySaved
                    ? 'bg-primary text-primary-foreground border-primary shadow hover:bg-primary/90'
                    : 'bg-card/60 hover:bg-card hover:border-primary/50 text-foreground'
                )}
              >
                <StoreIcon name="heart" fill={onlySaved ? 'currentColor' : 'none'} />
                {saved.length > 0 && <span className="text-xs font-bold">{saved.length}</span>}
              </Button>

              <div className="w-[170px] shrink-0">
                <Select value={sort} onValueChange={(val) => { setSort(val as CatalogSort); setPage(1); }}>
                  <SelectTrigger className="h-9 bg-card/60 border-border text-xs md:text-sm rounded-md hover:border-primary/50 transition-colors focus:ring-1 focus:ring-primary">
                    <SelectValue placeholder="Sort" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="featured">Sort: Featured</SelectItem>
                    <SelectItem value="newest">Newest</SelectItem>
                    <SelectItem value="price-asc">Price: Low to high</SelectItem>
                    <SelectItem value="price-desc">Price: High to low</SelectItem>
                    <SelectItem value="az">Name: A–Z</SelectItem>
                    <SelectItem value="za">Name: Z–A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="h-9 flex items-center gap-0.5 border border-border rounded-md p-0.5 bg-card/60 shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'h-[30px] w-[30px] rounded transition-all',
                    !list
                      ? 'bg-primary text-primary-foreground font-semibold shadow-sm hover:bg-primary hover:text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                  )}
                  aria-label="Grid view"
                  aria-pressed={!list}
                  onClick={() => setList(false)}
                >
                  <StoreIcon name="grid"/>
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'h-[30px] w-[30px] rounded transition-all',
                    list
                      ? 'bg-primary text-primary-foreground font-semibold shadow-sm hover:bg-primary hover:text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                  )}
                  aria-label="List view"
                  aria-pressed={list}
                  onClick={() => setList(true)}
                >
                  <StoreIcon name="list"/>
                </Button>
              </div>
            </div>
          </div>
          {filtered.length ? (
            <div
              id="catalog-results"
              className={cn(
                'grid',
                list
                  ? 'grid-cols-1 gap-3'
                  : 'grid-cols-2 gap-2.5 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(210px,1fr))] sm:gap-3.5'
              )}
              aria-describedby="catalog-result-count"
            >
              {pagination.visibleItems.map(item => (
                <SpriteCard key={`${item.source || 'static'}:${item.listing_id || item.id}`} item={item} saved={saved.includes(item.id)} onSave={() => { void toggleSaved(item); }} list={list}/>
              ))}
              {catalogLoading && Array.from({ length: 4 }).map((_, i) => (
                <SpriteCardSkeleton key={`loading-skeleton-${i}`} list={list} />
              ))}
            </div>
          ) : catalogLoading ? (
            <div
              id="catalog-results"
              className={cn(
                'grid',
                list
                  ? 'grid-cols-1 gap-3'
                  : 'grid-cols-2 gap-2.5 sm:grid-cols-[repeat(auto-fill,minmax(180px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(210px,1fr))] sm:gap-3.5'
              )}
              aria-describedby="catalog-result-count"
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <SpriteCardSkeleton key={`skeleton-only-${i}`} list={list} />
              ))}
            </div>
          ) : (
            <div id="catalog-results" className="min-h-[250px] p-10 flex flex-col items-center justify-center gap-3 text-center border border-dashed border-border rounded-md text-muted-foreground" aria-describedby="catalog-result-count">
              <StoreIcon name="search" width="32" height="32"/>
              <h2 className="text-xl font-medium text-foreground">No sprites found</h2>
              <p className="text-sm">Try another keyword or clear your filters.</p>
              <Button variant="default" onClick={resetFilters} className="rounded-md">Reset filters</Button>
            </div>
          )}
          <div className="flex items-center justify-between gap-4 py-6 text-sm text-muted-foreground border-t border-border/40 mt-4">
            <span>Showing {pagination.start}{pagination.end > pagination.start ? `–${pagination.end}` : ''} of {filtered.length} assets</span>
            {pagination.totalPages > 1 ? (
              <div className="flex items-center gap-2" aria-label="Catalog pagination">
                <Button variant="secondary" size="sm" disabled={pagination.page === 1} onClick={() => goToPage(pagination.page - 1)} className="rounded-md">
                  Previous
                </Button>
                <span aria-live="polite" className="text-xs text-muted-foreground">Page {pagination.page} of {pagination.totalPages}</span>
                <Button variant="secondary" size="sm" disabled={pagination.page === pagination.totalPages} onClick={() => goToPage(pagination.page + 1)} className="rounded-md">
                  Next
                </Button>
              </div>
            ) : (
              <span className="flex items-center gap-2">Made for your imagination <StoreIcon name="sparkle"/></span>
            )}
          </div>
        </div>
      </div>
    </section>
  </>;
}

function SpriteCardSkeleton({ list = false }: { list?: boolean }) {
  if (list) {
    return (
      <article className="relative rounded-md border border-border/70 bg-card/60 p-4 flex items-center gap-4 min-h-[120px] overflow-hidden" aria-hidden="true">
        <Skeleton className="h-24 w-24 rounded shrink-0" />
        <div className="flex-1 space-y-2.5">
          <Skeleton className="h-4 w-44 rounded" />
          <Skeleton className="h-3 w-28 rounded" />
          <Skeleton className="h-3 w-3/4 max-w-[500px] rounded" />
        </div>
        <Skeleton className="h-5 w-16 rounded shrink-0 hidden md:block" />
        <div className="flex items-center gap-2 shrink-0">
          <Skeleton className="h-8 w-8 rounded" />
          <Skeleton className="h-8 w-8 rounded" />
        </div>
      </article>
    );
  }

  return (
    <article className="relative rounded-md border border-border/70 bg-card/60 p-3 overflow-hidden flex flex-col justify-between min-h-[260px]" aria-hidden="true">
      <Skeleton className="h-5 w-14 rounded absolute top-3 left-3" />
      <Skeleton className="h-7 w-7 rounded absolute top-3 right-3" />
      <div className="h-36 flex items-center justify-center my-3">
        <Skeleton className="h-28 w-28 rounded" />
      </div>
      <div className="space-y-2 p-1 pt-2">
        <Skeleton className="h-4 w-3/4 rounded" />
        <Skeleton className="h-3 w-1/2 rounded" />
      </div>
      <Skeleton className="h-8 w-8 rounded absolute bottom-3 right-3" />
    </article>
  );
}

function SpriteCard({ item, saved, onSave, list = false }: { item: ManifestItem; saved: boolean; onSave: () => void; list?: boolean }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [error, setError] = useState('');
  const href = item.detail_href || `/assets/${item.id}`;

  useEffect(() => {
    if (item.preview_url) return;
    let cancelled = false;
    const controller = new AbortController();
    const image = new Image();
    const load = async () => {
      const animation = item.animations[item.default_animation];
      if (!animation?.png || !animation.json) throw new Error('Preview metadata unavailable');
      const base = `/free-library/${item.folder}`;
      const imageReady = new Promise<void>((resolve, reject) => { image.onload = () => resolve(); image.onerror = () => reject(new Error('Preview image unavailable')); image.src = `${base}/${animation.png}`; });
      const [response] = await Promise.all([fetch(`${base}/${animation.json}`, { signal: controller.signal }), imageReady]);
      if (!response.ok) throw new Error('Preview atlas unavailable');
      const atlas = await response.json(); const frame = atlas.frames?.['0'];
      if (!frame || ![frame.x, frame.y, frame.w, frame.h].every(Number.isFinite) || frame.x < 0 || frame.y < 0 || frame.w <= 0 || frame.h <= 0 || frame.x + frame.w > image.naturalWidth || frame.y + frame.h > image.naturalHeight) throw new Error('Invalid preview frame');
      if (cancelled) return;
      const canvas = canvasRef.current; const context = canvas?.getContext('2d');
      if (!canvas || !context) throw new Error('Canvas preview unavailable');
      context.clearRect(0, 0, 256, 256); context.imageSmoothingEnabled = false;
      const scale = Math.min(256 / frame.w, 256 / frame.h) * .9; const width = Math.round(frame.w * scale), height = Math.round(frame.h * scale);
      context.drawImage(image, frame.x, frame.y, frame.w, frame.h, Math.round((256-width)/2), Math.round((256-height)/2), width, height);
    };
    load().catch(reason => { if (!cancelled) setError(reason instanceof Error ? reason.message : 'Preview unavailable'); });
    return () => { cancelled = true; controller.abort(); image.onload = null; image.onerror = null; };
  }, [item]);

  const priceLabel = item.source === 'published'
    ? item.distribution_mode === 'free'
      ? 'Free · Production pack'
      : item.currency && Number.isSafeInteger(item.price_minor)
        ? formatMinorMoney(item.price_minor!, item.currency)
        : 'Price unavailable'
    : 'PNG · Spritesheet';

  if (item.source === 'published' && (!item.preview_url || !item.preview_mode)) throw new Error(`Published preview contract missing for ${item.id}`);

  if (list) {
    return (
      <article className="group relative rounded-md border border-[#2c3741] bg-gradient-to-br from-[#19232d] to-[#141c24] hover:border-[#7c7045] transition-all flex items-center p-3 gap-4 min-h-[120px] overflow-hidden">
        <Link href={href} className="flex items-center justify-center h-24 w-28 shrink-0 p-2" aria-label={`View ${item.name}`}>
          {error && !item.preview_url ? (
            <span className="text-destructive text-xs text-center" role="status">{error}</span>
          ) : item.preview_url && item.preview_mode ? (
            <AssetPreview src={item.preview_url} mode={item.preview_mode} alt={item.name} style={{ maxWidth: '100px', maxHeight: '100px', objectFit: 'contain', imageRendering: 'pixelated' }}/>
          ) : (
            <canvas ref={canvasRef} width="256" height="256" aria-label={item.name} role="img" className="w-[100px] h-[100px] object-contain image-rendering-pixelated" />
          )}
        </Link>
        <div className="flex-1 min-w-0 pr-4">
          <Link href={href}>
            <h2 className="text-sm font-medium text-foreground hover:text-primary transition-colors leading-snug truncate">{item.name}</h2>
          </Link>
          <p className="text-xs text-muted-foreground mt-1">{priceLabel}</p>
          <p className="text-xs text-muted-foreground/80 mt-1 line-clamp-2 max-w-xl">{item.description}</p>
          {error && item.preview_url ? <p className="text-destructive text-xs mt-1" role="alert">{error}</p> : null}
        </div>
        <Badge variant="tag" className="rounded px-2 py-0.5 text-[11px] shrink-0 mr-2">{item.category}</Badge>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn('h-8 w-8 rounded text-muted-foreground hover:text-primary', saved && 'text-primary')}
            onClick={onSave}
            aria-label={`${saved ? 'Unsave' : 'Save'} ${item.name}`}
            aria-pressed={saved}
          >
            <StoreIcon name="heart" fill={saved ? 'currentColor' : 'none'}/>
          </Button>
          {item.source === 'published' ? (
            <Button asChild variant="outline" size="icon" className="h-8 w-8 rounded border-border text-foreground hover:text-primary hover:border-primary">
              <Link href={href} aria-label={`Choose download format for ${item.name}`}>
                <StoreIcon name="download"/>
              </Link>
            </Button>
          ) : (
            <Button asChild variant="outline" size="icon" className="h-8 w-8 rounded border-border text-foreground hover:text-primary hover:border-primary">
              <a href={`/free-library/${item.folder}/${item.id}.zip`} download={`${item.id}.zip`} aria-label={`Download ${item.name}`}>
                <StoreIcon name="download"/>
              </a>
            </Button>
          )}
        </div>
      </article>
    );
  }

  return (
    <article className="group relative rounded-md border border-[#2c3741] bg-gradient-to-br from-[#19232d] to-[#141c24] hover:border-[#7c7045] hover:-translate-y-0.5 transition-all flex flex-col justify-between overflow-hidden">
      <Badge variant="tag" className="absolute left-3 top-3 rounded px-2 py-0.5 text-[11px] z-10">{item.category}</Badge>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className={cn('absolute top-2 right-2 h-8 w-8 rounded z-10 text-muted-foreground hover:text-primary', saved && 'text-primary')}
        onClick={onSave}
        aria-label={`${saved ? 'Unsave' : 'Save'} ${item.name}`}
        aria-pressed={saved}
      >
        <StoreIcon name="heart" fill={saved ? 'currentColor' : 'none'}/>
      </Button>
      <Link href={href} className="flex items-center justify-center h-40 pt-7 px-5" aria-label={`View ${item.name}`}>
        {error && !item.preview_url ? (
          <span className="text-destructive text-xs text-center" role="status">{error}</span>
        ) : item.preview_url && item.preview_mode ? (
          <AssetPreview src={item.preview_url} mode={item.preview_mode} alt={item.name} style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', imageRendering: 'pixelated' }}/>
        ) : (
          <canvas ref={canvasRef} width="256" height="256" aria-label={item.name} role="img" className="w-[140px] h-[140px] max-w-full object-contain image-rendering-pixelated" />
        )}
      </Link>
      <div className="p-3.5 pt-1 pr-12 min-h-[67px] relative">
        <Link href={href}>
          <h2 className="text-sm font-medium text-foreground hover:text-primary transition-colors leading-snug break-words">{item.name}</h2>
        </Link>
        <p className="text-xs text-muted-foreground mt-1">{priceLabel}</p>
        {error && item.preview_url ? <p className="text-destructive text-xs mt-1" role="alert">{error}</p> : null}
      </div>
      {item.source === 'published' ? (
        <Button asChild variant="outline" size="icon" className="absolute right-2.5 bottom-3 h-8 w-8 rounded border-border text-foreground hover:text-primary hover:border-primary">
          <Link href={href} aria-label={`Choose download format for ${item.name}`}>
            <StoreIcon name="download"/>
          </Link>
        </Button>
      ) : (
        <Button asChild variant="outline" size="icon" className="absolute right-2.5 bottom-3 h-8 w-8 rounded border-border text-foreground hover:text-primary hover:border-primary">
          <a href={`/free-library/${item.folder}/${item.id}.zip`} download={`${item.id}.zip`} aria-label={`Download ${item.name}`}>
            <StoreIcon name="download"/>
          </a>
        </Button>
      )}
    </article>
  );
}
